import {
  AssignmentResponseData,
  PayrollListSalaryComponentsQueryVariables,
} from 'api/gql-utils/salaryComponents/types';
import {
  deductionTypeFiltersMap,
  fnfSalaryComponentTypes,
  payTypeFiltersMap,
  statusFiltersMap,
  sectionToCategoryMap,
} from './constants';
import { ComponentType, DeductionsComponentType, SalaryComponent } from './types';

export const getTaxRegimeText = (
  underOldRegime: boolean = false,
  underNewRegime: boolean = false,
) => {
  if (underOldRegime && underNewRegime) {
    return 'Under Old & New Regime';
  } else if (underOldRegime) {
    return 'Under Old Regime';
  } else if (underNewRegime) {
    return 'Under New Regime';
  } else {
    return '';
  }
};

export const getTableItemCountLimits = (
  totalNumberOfItems: number,
  currentPage: number,
  itemsPerPage: number,
) => {
  if (totalNumberOfItems === 0) {
    return 'No items to display';
    // this case is unlikely to be shown as no data has a separate view
  }

  const startItem = currentPage * itemsPerPage + 1;
  const endItem = Math.min((currentPage + 1) * itemsPerPage, totalNumberOfItems);

  return `Showing ${startItem}-${endItem} components`;
};

export type SectionName = keyof typeof sectionToCategoryMap;

export const getQueryVariables = (
  section: SectionName,
  currentFilters: Record<string, string>,
): PayrollListSalaryComponentsQueryVariables => {
  const category = sectionToCategoryMap[section];

  const taxabilityFilter =
    currentFilters['taxability[]'] && currentFilters['taxability[]'].length > 0
      ? {
          taxExemptInOldRegime: currentFilters['taxability[]'].includes('old-regime'),
          taxExemptInNewRegime: currentFilters['taxability[]'].includes('new-regime'),
        }
      : undefined;

  // Determine payType based on section and filters
  let payType: any;

  if (category === 'EARNINGS_CATEGORY') {
    if (section === 'npb') {
      // For non-payable benefits, always filter by non_payable_benefit payType
      payType = payTypeFiltersMap['non_payable_benefit'];
    } else if (currentFilters.payType) {
      // For regular earnings, use the filter if provided
      payType = payTypeFiltersMap[currentFilters.payType];
    }
  } else if (category === 'DEDUCTIONS_CATEGORY' && currentFilters.deduction_type) {
    payType = deductionTypeFiltersMap[currentFilters.deduction_type];
  }

  return {
    category,
    status: statusFiltersMap[currentFilters.status] || statusFiltersMap.all,
    payType,
    taxability: taxabilityFilter,
  };
};

export const filterOutFnfComponents = <T extends SalaryComponent>(
  components: AssignmentResponseData<T>[],
) => {
  return components.filter((addition) => {
    const salaryComponent = addition?.componentDetails;
    if (fnfSalaryComponentTypes?.includes(salaryComponent?.componentType as ComponentType)) {
      return false;
    }

    return true;
  });
};

export const filterOutRecurringDeductions = (
  components: AssignmentResponseData<DeductionsComponentType>[],
) => {
  return components.filter((deduction) => {
    const salaryComponent = deduction.componentDetails;
    if (salaryComponent.settings.deductionDetails.deductionType === 'RECURRING') {
      return false;
    }
    return true;
  });
};
